import React, { useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import BasicInput from "../inputs/basic";
import Button from "../inputs/button";
import eyeOff from "../../assets/svgs/eye-off.svg";
import eyeOn from "../../assets/svgs/eyeopen.svg";
import { ResetPassword } from "../../services/passwordReset";
import PasswordChecklist from "react-password-checklist"

import checkmark from "../../assets/svgs/checkmark-green.svg";
import errorIcon from "../../assets/svgs/error.svg";

const PasswordRecoveryComponent = () => {
	const query = new URLSearchParams(useLocation().search);
	const nav = useNavigate();

	const [newPassword, setNewPassword] = useState("");
	const [isNewPassShown, setIsNewPassShown] = useState(false);
	const [error, setError] = useState(null);
	const [success, setSuccess] = useState(null);
	const [loaded, setLoaded] = useState(false);
	const [userEmail, setUserEmail] = useState("");
	const [countdown, setCountdown] = useState(5);
	const errorTimeoutRef = useRef(null);
	const toggleIsNewPassShown = () => {
		setIsNewPassShown(!isNewPassShown);
	};
	const [isValid, setIsValid] = useState(false);

	// Extract email from token
	React.useEffect(() => {
		const token = query.get("token");
		if (token) {
			try {
				// JWT tokens have three parts separated by dots
				const tokenParts = token.split(".");
				if (tokenParts.length === 3) {
					// Decode the payload (second part)
					const payload = JSON.parse(atob(tokenParts[1]));
					if (payload.email) {
						setUserEmail(payload.email);
					}
				}
			} catch (err) {
				console.error("Error decoding token:", err);
			}
		}
	}, []);

	// Countdown timer for redirect
	React.useEffect(() => {
		if (success && countdown > 0) {
			const timer = setTimeout(() => {
				setCountdown(countdown - 1);
			}, 1000);
			return () => clearTimeout(timer);
		} else if (success && countdown === 0) {
			nav("/login");
		}
	}, [success, countdown]);

	const handleReset = async (event) => {
		event.preventDefault();
		// Clear any existing error timeout
		if (errorTimeoutRef.current) {
			clearTimeout(errorTimeoutRef.current);
			errorTimeoutRef.current = null;
		}
		setError("");
		setLoaded(true);
		ResetPassword(query.get("token"), newPassword)
			.then((r) => {
				// Response is ok, show success message
				r.json().then((data) => {
					setSuccess(data.message || "Password reset successfully");
					setLoaded(false);
				});
			})
			.catch((error) => {
				// Handle error thrown by apiClient for non-ok responses
				const errorMessage = error.data?.message || error.message || "Failed to reset password";
				setError(errorMessage);
				setLoaded(false);
				errorTimeoutRef.current = setTimeout(() => {
					setError("");
					errorTimeoutRef.current = null;
				}, 5000);
			});
	};
	return (
			<div className="login-container-box">
				{success ? (
					<div className="success-container">
						<div className="login-inputs">
							<h2 className="success-title">Password Reset Successful</h2>
							<p className="redirect-message">
								Redirecting to login in {countdown} second{countdown !== 1 ? 's' : ''}...
							</p>
						</div>
					</div>
				) : (
					<form id="resetPassword" onSubmit={handleReset}>
						<div className="login-inputs">
								{userEmail && (
									<BasicInput
										label="Email"
										id="email"
										name="email"
										value={userEmail}
										type="email"
										disabled={true}
										readOnly={true}
									/>
								)}
								<div className="password-input">
									<BasicInput
										label="New Password"
										id="password"
										name="password"
										value={newPassword}
										type={isNewPassShown ? "text" : "password"}
										autoComplete="new-password"
										onInput={(e) => setNewPassword(e.target.value)}
										disabled={false}
									/>
									{!isNewPassShown ? (
										<img
											className="passwordIcon-reset"
											src={eyeOff}
											onClick={toggleIsNewPassShown}
										/>
									) : (
										<img
											className="passwordIcon-reset"
											src={eyeOn}
											onClick={toggleIsNewPassShown}
										/>
									)}
								</div>
								{error && <div className="login-error">{error}</div>}
								<div className="password-checklist">
									<PasswordChecklist
										rules={["lowercase","capital","number","specialChar","noSpaces","minLength"]}
										minLength={8}
										value={newPassword}
										onChange={setIsValid}
										messages={{
											minLength: "Minimum of 8 characters",
											specialChar: "At least one special character",
											number: "At least one number",
											capital: "At least one uppercase letter",
											lowercase: "At least one lowercase letter",
											noSpaces: "Does not contain any spaces"
										}}
										iconComponents={{ValidIcon: <img src={checkmark} className="icon" />,
														 InvalidIcon: <img src={errorIcon} className="icon" />}}
									/>
								</div>
						</div>
						<div className="divider" />
						<div className="button-container-reset">
							<Button
								loaded={loaded}
								text="Reset"
								style="dark"
								type="submit"
								disabled={loaded || !isValid}
							/>
						</div>
					</form>
				)}
			</div>
	);
};

export default PasswordRecoveryComponent;
